// FIXED: VibeOnboarding.jsx - Mobile-responsive version
import React, { useState, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Brain, Shield, Zap, Star, Heart, ChevronRight, 
  User, Mail, Eye, EyeOff, Lock, Sparkles, 
  CheckCircle, ArrowLeft, Gift, Crown,
} from 'lucide-react';
import { usePrivacyAuth } from '@/context/PrivacyAuthContext';
import { useApp } from '@/context/AppContext';

// FIXED: Mobile-responsive Card component
const Card = ({ children, className, onClick }) => (
  <div 
    className={`bg-slate-800/50 backdrop-blur-lg rounded-xl border border-slate-700/50 ${className}`} 
    onClick={onClick}
  >
    {children}
  </div>
);

// FIXED: Mobile-responsive Button component
const Button = ({ children, onClick, disabled, variant, size, className, type = 'button' }) => {
  const baseClasses = 'px-3 py-2 sm:px-4 sm:py-2 rounded-lg font-semibold transition-all duration-200 flex items-center justify-center text-sm sm:text-base';
  const variantClasses = {
    secondary: 'bg-slate-700 hover:bg-slate-600 text-white border border-slate-600',
    primary: 'bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white',
  };
  const sizeClasses = {
    lg: 'px-4 py-3 sm:px-6 sm:py-3 text-base sm:text-lg',
  };
  
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={`${baseClasses} ${variantClasses[variant] || variantClasses.primary} ${sizeClasses[size] || ''} ${className} ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
    >
      {children}
    </button>
  );
};

const VibeOnboarding = React.memo(({ onComplete = () => {} }) => {
  const [step, setStep] = useState(1);
  const [signupType, setSignupType] = useState(null);
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    ageConfirm: false,
    privacyAccept: false,
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const { quickSetup, createAccount } = usePrivacyAuth();
  const { setCurrentView } = useApp();


  // Defensive input handler with proper debouncing
  const handleInputChange = useCallback((field, value) => {
    // Debug logging in development
    if (import.meta.env.MODE === 'development') {
      console.log(`🔧 Input change: ${field} = "${value}"`);
      console.log('🔧 Component is re-rendering input handler');
    }
    
    // Ensure we have valid input
    if (typeof value !== 'string') return;
    
    setFormData(prevData => {
      // Don't update if value hasn't changed
      if (prevData[field] === value) {
        if (import.meta.env.MODE === 'development') {
          console.log(`🔧 No change needed for ${field}`);
        }
        return prevData;
      }
      
      if (import.meta.env.MODE === 'development') {
        console.log(`🔧 Updating ${field} from "${prevData[field]}" to "${value}"`);
      }
      
      return {
        ...prevData,
        [field]: value,
      };
    });
  }, []);

  // Clear error function
  const clearError = useCallback((field) => {
    setErrors(prevErrors => {
      if (!prevErrors[field]) return prevErrors;
      const newErrors = { ...prevErrors };
      delete newErrors[field];
      return newErrors;
    });
  }, []);

  // Validation function
  const validateForm = useCallback(() => {
    const newErrors = {};
    
    if (!formData.username.trim()) {
      newErrors.username = 'Username is required';
    } else if (formData.username.length < 3) {
      newErrors.username = 'Username must be at least 3 characters';
    }
    
    if (signupType === 'email') {
      if (!formData.email.trim()) {
        newErrors.email = 'Email is required';
      } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
        newErrors.email = 'Please enter a valid email';
      }
      
      if (!formData.password) {
        newErrors.password = 'Password is required';
      } else if (formData.password.length < 6) {
        newErrors.password = 'Password must be at least 6 characters';
      }
      
      if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = 'Passwords do not match';
      }
    }
    
    if (!formData.ageConfirm) {
      newErrors.ageConfirm = 'Age confirmation is required';
    }
    
    if (!formData.privacyAccept) {
      newErrors.privacyAccept = 'Privacy policy acceptance is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, signupType]);

  // Signup handler
  const handleSignup = useCallback(async () => {
    if (!validateForm()) return;
    
    setLoading(true);
    setErrors({});
    
    try {
      let result;
      
      if (signupType === 'anonymous') {
        result = await quickSetup(formData.username);
      } else {
        result = await createAccount(formData.username, formData.password, formData.email);
      }
      
      if (result.success) {
        setStep(4);
        setTimeout(() => {
          onComplete();
        }, 3000);
      } else {
        setErrors({ general: result.error || 'Signup failed. Please try again.' });
      }
    } catch (error) {
      console.error('Signup error:', error);
      setErrors({ general: 'Something went wrong. Please try again.' });
    } finally {
      setLoading(false);
    }
  }, [validateForm, signupType, formData.username, formData.password, quickSetup, createAccount, onComplete]);

  const WelcomeStep = () => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="text-center space-y-6 px-4 sm:px-6"
    >
      {/* Hero Icon */}
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
        className="relative"
      >
        <div className="w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 bg-gradient-to-br from-purple-500 to-indigo-500 rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6 relative">
          <Brain className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 text-white" />
          <motion.div
            className="absolute inset-0 rounded-full border-2 border-purple-400/50"
            animate={{ scale: [1, 1.2, 1] }}
            transition={{ duration: 2, repeat: Infinity }}
          />
        </div>
        <div className="absolute -top-1 -right-1 sm:-top-2 sm:-right-2">
          <motion.div
            animate={{ rotate: [0, 15, -15, 0] }}
            transition={{ duration: 2, repeat: Infinity, delay: 1 }}
            className="text-xl sm:text-2xl"
          >
            ✨
          </motion.div>
        </div>
      </motion.div>
      
      {/* Titles */}
      <div className="space-y-2 sm:space-y-4">
        <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white">
          Welcome to Inner Voice
        </h1>
        <h2 className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-indigo-400">
          Vibe Edition ✨
        </h2>
        <p className="text-sm sm:text-base lg:text-lg text-slate-300 max-w-2xl mx-auto leading-relaxed px-2">
          Transform your self-talk with science-backed techniques designed specifically for your generation. 
          No boring lectures, just real tools that actually work.
        </p>
      </div>

      {/* Feature Grid - FIXED for mobile */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 max-w-4xl mx-auto">
        {[
          {
            icon: Zap,
            title: 'Quick & Fun',
            description: 'Micro-lessons that fit your life',
            color: 'from-yellow-500 to-orange-500',
          },
          {
            icon: Shield,
            title: 'Your Privacy First',
            description: 'Data stays on your device',
            color: 'from-green-500 to-emerald-500',
          },
          {
            icon: Star,
            title: 'Actually Works',
            description: 'Science-backed techniques',
            color: 'from-blue-500 to-indigo-500',
          },
        ].map((feature, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 + index * 0.1 }}
            className="p-3 sm:p-4 bg-white/5 rounded-xl border border-white/10"
          >
            <div className={`w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br ${feature.color} rounded-lg flex items-center justify-center mx-auto mb-2 sm:mb-3`}>
              <feature.icon className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
            </div>
            <h3 className="text-white font-semibold mb-1 sm:mb-2 text-sm sm:text-base">{feature.title}</h3>
            <p className="text-slate-400 text-xs sm:text-sm">{feature.description}</p>
          </motion.div>
        ))}
      </div>

      {/* CTA Button */}
      <Button 
        onClick={() => setStep(2)}
        size="lg"
        className="min-w-[180px] sm:min-w-[200px] bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700"
      >
        Let's Start
        <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5 ml-2" />
      </Button>
    </motion.div>
  );

  const PrivacyChoiceStep = () => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="max-w-4xl mx-auto space-y-6 sm:space-y-8 px-4 sm:px-6"
    >
      {/* Header */}
      <div className="text-center">
        <div className="text-3xl sm:text-4xl mb-3 sm:mb-4">🔐</div>
        <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-white mb-3 sm:mb-4">
          Choose Your Privacy Level
        </h2>
        <p className="text-sm sm:text-base lg:text-lg text-slate-300 mb-6 sm:mb-8">
          Both options are completely secure. Pick what feels right for you.
        </p>
      </div>

      {/* Option Cards - FIXED for mobile stacking */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
        {/* Anonymous Option */}
        <motion.div
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <Card 
            className={`p-4 sm:p-6 cursor-pointer transition-all border-2 ${
              signupType === 'anonymous' 
                ? 'border-green-500 bg-green-500/10' 
                : 'border-slate-600 hover:border-green-400'
            }`}
            onClick={() => {
              setSignupType('anonymous');
              setTimeout(() => setStep(3), 300);
            }}
          >
            <div className="text-center space-y-3 sm:space-y-4">
              <div className="w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 bg-gradient-to-br from-green-500 to-emerald-500 rounded-xl flex items-center justify-center mx-auto">
                <Shield className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 text-white" />
              </div>
              
              <div>
                <h3 className="text-lg sm:text-xl font-bold text-white mb-2">
                  Maximum Privacy
                </h3>
                <p className="text-sm sm:text-base text-slate-300 mb-3 sm:mb-4">
                  Just pick a username and start your journey immediately. Perfect for trying it out.
                </p>
              </div>

              {/* Features List - FIXED spacing */}
              <div className="space-y-1 sm:space-y-2 text-left">
                {[
                  'No personal info required',
                  'Data stays on your device', 
                  'Can\'t recover if lost',
                  'Perfect for trying it out',
                ].map((feature, idx) => (
                  <div key={idx} className="flex items-center gap-2 text-green-300">
                    <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
                    <span className="text-xs sm:text-sm">{feature}</span>
                  </div>
                ))}
              </div>

              <button
                className="w-full bg-green-500/20 hover:bg-green-500/30 rounded-lg p-2 sm:p-3 border border-green-500/30 transition-colors"
                onClick={(e) => {
                  e.stopPropagation();
                  setSignupType('anonymous');
                  setTimeout(() => setStep(3), 300);
                }}
              >
                <div className="text-green-300 font-medium text-xs sm:text-sm">
                  🌱 Recommended for first-time users
                </div>
              </button>
            </div>
          </Card>
        </motion.div>

        {/* Email Option */}
        <motion.div
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <Card 
            className={`p-4 sm:p-6 cursor-pointer transition-all border-2 ${
              signupType === 'email' 
                ? 'border-purple-500 bg-purple-500/10' 
                : 'border-slate-600 hover:border-purple-400'
            }`}
            onClick={() => {
              setSignupType('email');
              setTimeout(() => setStep(3), 300);
            }}
          >
            <div className="text-center space-y-3 sm:space-y-4">
              <div className="w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 bg-gradient-to-br from-purple-500 to-indigo-500 rounded-xl flex items-center justify-center mx-auto">
                <Mail className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 text-white" />
              </div>
              
              <div>
                <h3 className="text-lg sm:text-xl font-bold text-white mb-2">
                  Full Experience
                </h3>
                <p className="text-sm sm:text-base text-slate-300 mb-3 sm:mb-4">
                  Secure account with backup and sync across devices
                </p>
              </div>

              {/* Features List - FIXED spacing */}
              <div className="space-y-1 sm:space-y-2 text-left">
                {[
                  'Account recovery available',
                  'Sync across devices',
                  'Progress never lost', 
                  'Optional progress sharing',
                ].map((feature, idx) => (
                  <div key={idx} className="flex items-center gap-2 text-purple-300">
                    <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
                    <span className="text-xs sm:text-sm">{feature}</span>
                  </div>
                ))}
              </div>

              <button
                className="w-full bg-purple-500/20 hover:bg-purple-500/30 rounded-lg p-2 sm:p-3 border border-purple-500/30 transition-colors"
                onClick={(e) => {
                  e.stopPropagation();
                  setSignupType('email');
                  setTimeout(() => setStep(3), 300);
                }}
              >
                <div className="text-purple-300 font-medium text-xs sm:text-sm">
                  🚀 Best for committed users
                </div>
              </button>
            </div>
          </Card>
        </motion.div>
      </div>

      {/* Navigation Buttons - FIXED for mobile */}
      <div className="flex flex-col sm:flex-row justify-center gap-3 sm:gap-4">
        <Button 
          variant="secondary" 
          onClick={() => setStep(1)}
          className="w-full sm:w-auto"
        >
          <ArrowLeft className="w-3 h-3 sm:w-4 sm:h-4 mr-2" />
          Back
        </Button>
        
        <Button 
          onClick={() => setStep(3)}
          disabled={!signupType}
          size="lg"
          className={`w-full sm:w-auto min-w-[200px] ${
            signupType === 'anonymous' 
              ? 'bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700'
              : 'bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700'
          }`}
        >
          Continue with {signupType === 'anonymous' ? 'Anonymous' : 'Email'} Account
          <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5 ml-2" />
        </Button>
      </div>
    </motion.div>
  );

  const SignupFormStep = () => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="max-w-2xl mx-auto px-4 sm:px-6"
    >
      {/* Header */}
      <div className="text-center mb-6 sm:mb-8">
        <div className={`w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 bg-gradient-to-br ${
          signupType === 'anonymous' 
            ? 'from-green-500 to-emerald-500' 
            : 'from-purple-500 to-indigo-500'
        } rounded-xl flex items-center justify-center mx-auto mb-3 sm:mb-4`}>
          {signupType === 'anonymous' ? (
            <Shield className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 text-white" />
          ) : (
            <Mail className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 text-white" />
          )}
        </div>
        <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-white mb-2">
          {signupType === 'anonymous' ? 'Anonymous Setup' : 'Create Your Account'}
        </h2>
        <p className="text-sm sm:text-base text-slate-300">
          {signupType === 'anonymous' 
            ? 'Just pick a username - no personal info needed!'
            : 'Set up your secure account with email backup'
          }
        </p>
      </div>

      {/* Form Card - FIXED for mobile scrolling */}
      <Card className="p-4 sm:p-6 lg:p-8">
        <form onSubmit={(e) => {
          e.preventDefault();
          handleSignup();
        }} className="space-y-4 sm:space-y-6">
          {/* Username Field */}
          <div>
            <label className="block text-white font-medium mb-2 text-sm sm:text-base">
              <User className="w-3 h-3 sm:w-4 sm:h-4 inline mr-2" />
              Username
            </label>
            <input
              type="text"
              value={formData.username}
              onChange={(e) => {
                e.preventDefault();
                e.stopPropagation();
                const {value} = e.target;
                handleInputChange('username', value);
                clearError('username');
              }}
              placeholder="ChooseAnyUsername"
              className={`w-full p-2 sm:p-3 bg-slate-700 border rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 text-sm sm:text-base ${
                errors.username 
                  ? 'border-red-500 focus:ring-red-500/20' 
                  : 'border-slate-600 focus:ring-purple-500/20 focus:border-purple-500'
              }`}
              autoComplete="username"
            />
            {errors.username && (
              <p className="text-red-400 text-xs sm:text-sm mt-1">{errors.username}</p>
            )}
            <p className="text-slate-400 text-xs mt-1">
              Like a gaming username - whatever feels right!
            </p>
          </div>

          {/* Email Fields (only for email signup) */}
          {signupType === 'email' && (
            <>
              <div>
                <label className="block text-white font-medium mb-2 text-sm sm:text-base">
                  <Mail className="w-3 h-3 sm:w-4 sm:h-4 inline mr-2" />
                  Email Address
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    const {value} = e.target;
                    handleInputChange('email', value);
                    clearError('email');
                  }}
                  placeholder="your.email@example.com"
                  className={`w-full p-2 sm:p-3 bg-slate-700 border rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 text-sm sm:text-base ${
                    errors.email 
                      ? 'border-red-500 focus:ring-red-500/20' 
                      : 'border-slate-600 focus:ring-purple-500/20 focus:border-purple-500'
                  }`}
                  autoComplete="email"
                />
                {errors.email && (
                  <p className="text-red-400 text-xs sm:text-sm mt-1">{errors.email}</p>
                )}
              </div>

              <div>
                <label className="block text-white font-medium mb-2 text-sm sm:text-base">
                  <Lock className="w-3 h-3 sm:w-4 sm:h-4 inline mr-2" />
                  Password
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      const {value} = e.target;
                      handleInputChange('password', value);
                      clearError('password');
                    }}
                    placeholder="Choose a secure password"
                    className={`w-full p-2 sm:p-3 bg-slate-700 border rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 pr-10 sm:pr-12 text-sm sm:text-base ${
                      errors.password 
                        ? 'border-red-500 focus:ring-red-500/20' 
                        : 'border-slate-600 focus:ring-purple-500/20 focus:border-purple-500'
                    }`}
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-2 sm:right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-white"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4 sm:w-5 sm:h-5" /> : <Eye className="w-4 h-4 sm:w-5 sm:h-5" />}
                  </button>
                </div>
                {errors.password && (
                  <p className="text-red-400 text-xs sm:text-sm mt-1">{errors.password}</p>
                )}
              </div>

              <div>
                <label className="block text-white font-medium mb-2 text-sm sm:text-base">
                  <Lock className="w-3 h-3 sm:w-4 sm:h-4 inline mr-2" />
                  Confirm Password
                </label>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={formData.confirmPassword}
                  onChange={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    const {value} = e.target;
                    handleInputChange('confirmPassword', value);
                    clearError('confirmPassword');
                  }}
                  placeholder="Confirm your password"
                  className={`w-full p-2 sm:p-3 bg-slate-700 border rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 text-sm sm:text-base ${
                    errors.confirmPassword 
                      ? 'border-red-500 focus:ring-red-500/20' 
                      : 'border-slate-600 focus:ring-purple-500/20 focus:border-purple-500'
                  }`}
                  autoComplete="new-password"
                />
                {errors.confirmPassword && (
                  <p className="text-red-400 text-xs sm:text-sm mt-1">{errors.confirmPassword}</p>
                )}
              </div>
            </>
          )}

          {/* Checkboxes - FIXED for mobile touch targets */}
          <div className="space-y-3 sm:space-y-4">
            <label className="flex items-start gap-3">
              <input
                type="checkbox"
                checked={formData.ageConfirm}
                onChange={(e) => {
                  handleInputChange('ageConfirm', e.target.checked);
                  clearError('ageConfirm');
                }}
                className="mt-1 w-4 h-4 sm:w-5 sm:h-5 text-purple-600 bg-slate-700 border-slate-600 rounded focus:ring-purple-500 focus:ring-2 flex-shrink-0"
              />
              <div>
                <span className="text-white text-sm sm:text-base">
                  I confirm that I am at least 13 years old
                </span>
                <p className="text-slate-400 text-xs mt-1">
                  Required for account creation
                </p>
              </div>
            </label>
            {errors.ageConfirm && (
              <p className="text-red-400 text-xs sm:text-sm">{errors.ageConfirm}</p>
            )}

            <label className="flex items-start gap-3">
              <input
                type="checkbox"
                checked={formData.privacyAccept}
                onChange={(e) => {
                  handleInputChange('privacyAccept', e.target.checked);
                  clearError('privacyAccept');
                }}
                className="mt-1 w-4 h-4 sm:w-5 sm:h-5 text-purple-600 bg-slate-700 border-slate-600 rounded focus:ring-purple-500 focus:ring-2 flex-shrink-0"
              />
              <div>
                <span className="text-white text-sm sm:text-base">
                  I understand the privacy policy and terms
                </span>
                <p className="text-slate-400 text-xs mt-1">
                  We respect your privacy and keep data secure
                </p>
              </div>
            </label>
            {errors.privacyAccept && (
              <p className="text-red-400 text-xs sm:text-sm">{errors.privacyAccept}</p>
            )}
          </div>

          {/* General Error */}
          {errors.general && (
            <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
              <p className="text-red-400 text-xs sm:text-sm">{errors.general}</p>
            </div>
          )}

          {/* Privacy Reminder */}
          <div className={`p-3 sm:p-4 rounded-lg border ${
            signupType === 'anonymous' 
              ? 'bg-green-500/10 border-green-500/30' 
              : 'bg-purple-500/10 border-purple-500/30'
          }`}>
            <div className="flex items-start gap-3">
              <Shield className={`w-4 h-4 sm:w-5 sm:h-5 mt-0.5 flex-shrink-0 ${
                signupType === 'anonymous' ? 'text-green-400' : 'text-purple-400'
              }`} />
              <div>
                <h4 className={`font-medium mb-1 text-sm sm:text-base ${
                  signupType === 'anonymous' ? 'text-green-300' : 'text-purple-300'
                }`}>
                  {signupType === 'anonymous' ? 'Maximum Privacy' : 'Secure & Private'}
                </h4>
                <p className="text-slate-300 text-xs sm:text-sm">
                  {signupType === 'anonymous' 
                    ? 'Your data stays completely on your device. We never see your personal information.'
                    : 'Your email is encrypted and only used for account recovery. We never sell or share your data.'
                  }
                </p>
              </div>
            </div>
          </div>

          {/* Submit Buttons - FIXED for mobile */}
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
            <Button 
              type="button"
              variant="secondary" 
              onClick={() => setStep(2)}
              disabled={loading}
              className="w-full sm:flex-1"
            >
              <ArrowLeft className="w-3 h-3 sm:w-4 sm:h-4 mr-2" />
              Back
            </Button>
            
            <Button 
              type="submit"
              disabled={loading}
              className={`w-full sm:flex-1 ${
                signupType === 'anonymous' 
                  ? 'bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700'
                  : 'bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700'
              }`}
            >
              {loading ? (
                'Creating Account...'
              ) : (
                <>
                  <Sparkles className="w-3 h-3 sm:w-4 sm:h-4 mr-2" />
                  Create My Account
                </>
              )}
            </Button>
            
            {/* Login Instead Option */}
            <div className="text-center pt-4 border-t border-slate-600">
              <p className="text-slate-400 text-sm mb-2">
                Already have an account?
              </p>
              <div className="flex gap-2">
                <Button 
                  variant="ghost"
                  onClick={() => setCurrentView('home')}
                  className="text-slate-400 hover:text-white flex-1"
                >
                  Back to Home
                </Button>
                <Button 
                  variant="secondary"
                  onClick={() => setCurrentView('login')}
                  className="text-purple-300 hover:text-white flex-1"
                >
                  Login Instead
                </Button>
              </div>
            </div>
          </div>
        </form>
      </Card>
    </motion.div>
  );

  const SuccessStep = () => (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ type: 'spring', stiffness: 200 }}
      className="text-center space-y-6 sm:space-y-8 px-4"
    >
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ delay: 0.2, type: 'spring', stiffness: 300 }}
        className="relative"
      >
        <div className="text-4xl sm:text-5xl md:text-6xl mb-4">🎉</div>
        <motion.div
          animate={{ scale: [1, 1.1, 1] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="absolute -top-2 sm:-top-4 -right-2 sm:-right-4 text-xl sm:text-2xl"
        >
          ✨
        </motion.div>
      </motion.div>

      <div>
        <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3 sm:mb-4">
          Welcome to Your Journey!
        </h2>
        <p className="text-sm sm:text-base lg:text-lg text-slate-300 max-w-2xl mx-auto px-2">
          Your account is ready! You're about to discover powerful techniques that will transform 
          how you talk to yourself and boost your confidence.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 max-w-2xl mx-auto">
        {[
          { icon: Brain, text: 'Take your vibe check', color: 'from-purple-500 to-indigo-500' },
          { icon: Heart, text: 'Learn at your pace', color: 'from-pink-500 to-rose-500' },
          { icon: Star, text: 'Transform your mindset', color: 'from-yellow-500 to-orange-500' },
        ].map((item, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 + index * 0.1 }}
            className="p-3 sm:p-4 bg-white/5 rounded-lg border border-white/10"
          >
            <div className={`w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br ${item.color} rounded-lg flex items-center justify-center mx-auto mb-2`}>
              <item.icon className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
            </div>
            <p className="text-white text-xs sm:text-sm font-medium">{item.text}</p>
          </motion.div>
        ))}
      </div>

      <div className="text-xs sm:text-sm text-purple-300">
        Taking you to your dashboard now...
      </div>
    </motion.div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-3 sm:p-4 overflow-y-auto">
      {/* FIXED: Mobile-responsive container with proper constraints */}
      <div className="w-full max-w-6xl my-auto py-4 sm:py-8 min-h-0">
        <AnimatePresence mode="wait">
          {step === 1 && <WelcomeStep key="welcome" />}
          {step === 2 && <PrivacyChoiceStep key="privacy" />}
          {step === 3 && <SignupFormStep key="signup" />}
          {step === 4 && <SuccessStep key="success" />}
        </AnimatePresence>
      </div>
    </div>
  );
});

export default VibeOnboarding;